import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Shield, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('admin@dhihospitality.com');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [authConfig, setAuthConfig] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      fetch(`${API_URL}/api/admin/me`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => { if (r.ok) navigate('/admin'); });
    }
    fetch(`${API_URL}/api/admin/auth/config`)
      .then(r => r.json())
      .then(setAuthConfig)
      .catch(() => setAuthConfig({ azureConfigured: false }));
  }, [navigate]);

  // Handle Azure callback
  useEffect(() => {
    const code = searchParams.get('code');
    if (!code || !authConfig) return;
    setLoading(true);
    fetch(`${API_URL}/api/admin/auth/callback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, redirect_uri: `${window.location.origin}/admin/login` }),
    })
      .then(r => { if (!r.ok) throw new Error('Microsoft authentication failed'); return r.json(); })
      .then(d => { localStorage.setItem('adminToken', d.token); localStorage.setItem('adminEmail', d.email); navigate('/admin'); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [searchParams, authConfig, navigate]);

  const handleDevLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/admin/auth/dev-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!r.ok) {
        const d = await r.json();
        throw new Error(d.detail || 'Login failed');
      }
      const d = await r.json();
      localStorage.setItem('adminToken', d.token);
      localStorage.setItem('adminEmail', d.email);
      navigate('/admin');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoftLogin = () => {
    if (!authConfig) return;
    const params = new URLSearchParams({
      client_id: authConfig.clientId,
      response_type: 'code',
      redirect_uri: `${window.location.origin}/admin/login`,
      scope: 'openid profile email',
      response_mode: 'query',
    });
    window.location.href = `https://login.microsoftonline.com/${authConfig.tenantId}/oauth2/v2.0/authorize?${params}`;
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0A0F2E, #111B5E)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'DM Sans', sans-serif" }} data-testid="admin-login-page">
      <div style={{ width: 400, maxWidth: '90vw', background: '#fff', borderRadius: 16, padding: '40px 36px', boxShadow: '0 12px 40px rgba(0,0,0,0.3)' }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: 'linear-gradient(135deg, #2D4DE0, #111B5E)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
            <Shield size={24} color="#fff" />
          </div>
          <h1 style={{ fontFamily: "'Outfit', sans-serif", fontSize: 22, fontWeight: 800, color: '#0A0A1A', margin: '0 0 4px' }}>AI Audit Admin</h1>
          <p style={{ fontSize: 13, color: 'rgba(10,10,26,0.4)', margin: 0 }}>dhi Hospitality &middot; Internal Access</p>
        </div>

        {error && (
          <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#DC2626' }} data-testid="login-error">{error}</div>
        )}

        {authConfig?.azureConfigured && (
          <button onClick={handleMicrosoftLogin} disabled={loading} style={{ width: '100%', padding: '12px 16px', border: '1px solid rgba(10,10,26,0.12)', borderRadius: 10, background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, fontSize: 14, fontWeight: 600, color: '#0A0A1A', marginBottom: 20 }} data-testid="btn-microsoft-login">
            <svg width="20" height="20" viewBox="0 0 21 21"><rect x="1" y="1" width="9" height="9" fill="#F25022"/><rect x="11" y="1" width="9" height="9" fill="#7FBA00"/><rect x="1" y="11" width="9" height="9" fill="#00A4EF"/><rect x="11" y="11" width="9" height="9" fill="#FFB900"/></svg>
            {loading ? 'Signing in...' : 'Sign in with Microsoft'}
          </button>
        )}

        {authConfig?.azureConfigured && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '0 0 20px' }}>
            <div style={{ flex: 1, height: 1, background: 'rgba(10,10,26,0.08)' }} />
            <span style={{ fontSize: 11, color: 'rgba(10,10,26,0.3)', fontWeight: 600 }}>OR</span>
            <div style={{ flex: 1, height: 1, background: 'rgba(10,10,26,0.08)' }} />
          </div>
        )}

        <form onSubmit={handleDevLogin}>
          <label style={{ display: 'block', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(10,10,26,0.4)', marginBottom: 6 }}>Email Address</label>
          <input
            type="email" value={email} onChange={e => setEmail(e.target.value)}
            placeholder="admin@dhihospitality.com"
            style={{ width: '100%', padding: '12px 14px', border: '1px solid rgba(10,10,26,0.1)', borderRadius: 8, fontSize: 14, color: '#0A0A1A', background: '#F8F9FF', outline: 'none', boxSizing: 'border-box', fontFamily: "'DM Sans', sans-serif", marginBottom: 16 }}
            data-testid="login-email-input"
          />
          <button type="submit" disabled={loading} style={{ width: '100%', padding: '12px 16px', border: 'none', borderRadius: 10, background: '#2D4DE0', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontFamily: "'DM Sans', sans-serif" }} data-testid="btn-dev-login">
            {loading && <Loader2 size={16} className="animate-spin" />}
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <p style={{ textAlign: 'center', fontSize: 11, color: 'rgba(10,10,26,0.3)', marginTop: 20, marginBottom: 0 }}>
          Access restricted to @dhihospitality.com
        </p>
      </div>
    </div>
  );
}
